package dev.danko.roblox_craft_ai.components

import dev.danko.roblox_craft_ai.state.Txt
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import dev.danko.roblox_craft_ai.shared.model.AssetType
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.ModalDialog
import dev.danko.roblox_craft_ai.uikit.components.ModalDialogConfirmActions
import dev.danko.roblox_craft_ai.uikit.components.StatusChip
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input

val SyncModalTypeChipStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .padding(topBottom = 2.px, leftRight = 0.5.cssRem)
        .borderRadius(0.375.cssRem)
        .fontSize(0.75.cssRem)
        .backgroundColor(palette.surfaceElevated)
        .color(palette.textSecondary)
        .border(1.px, LineStyle.Solid, palette.border)
}

private const val SKIP_SYNC_CONFIRM_KEY = "robloxCraft.skipSyncConfirm"

fun isSkipSyncConfirmEnabled(): Boolean {
    return window.asDynamic().localStorage.getItem(SKIP_SYNC_CONFIRM_KEY) == "true"
}

fun setSkipSyncConfirmEnabled(enabled: Boolean) {
    if (enabled) {
        window.asDynamic().localStorage.setItem(SKIP_SYNC_CONFIRM_KEY, "true")
    } else {
        window.asDynamic().localStorage.removeItem(SKIP_SYNC_CONFIRM_KEY)
    }
}

private fun copyToClipboard(text: String) {
    try {
        window.navigator.asDynamic().clipboard.writeText(text)
    } catch (_: Throwable) { }
}

@Composable
fun SyncConfirmationModal(
    visible: Boolean,
    asset: AssetResponse?,
    onCloseRequest: () -> Unit,
    onConfirmSync: (AssetResponse) -> Unit
) {
    if (!visible || asset == null) return

    var isSyncing by remember { mutableStateOf(false) }
    var skipConfirm by remember { mutableStateOf(isSkipSyncConfirmEnabled()) }
    val palette = com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toAppPalette()

    fun handleConfirm() {
        if (isSyncing) return
        isSyncing = true
        onConfirmSync(asset)
        isSyncing = false
        onCloseRequest()
    }

    val displayPrompt = if (asset.prompt.isBlank()) {
        Txt.syncModal.untitledAsset
    } else if (asset.prompt.length > 80) {
        asset.prompt.take(80) + "…"
    } else {
        asset.prompt
    }

    val shortId = if (asset.id.length > 12) asset.id.take(12) + "…" else asset.id

    val typeIcon = when (asset.assetType) {
        AssetType.LUAU_SCRIPT -> "🟣"
        AssetType.MESH_3D -> "🏗️"
        AssetType.ANIMATION -> "🎬"
        AssetType.ROJO_PROJECT -> "📦"
    }

    val typeLabel = when (asset.assetType) {
        AssetType.LUAU_SCRIPT -> Txt.assetTypes.name(AssetType.LUAU_SCRIPT)
        AssetType.MESH_3D -> Txt.assetTypes.name(AssetType.MESH_3D)
        AssetType.ANIMATION -> Txt.assetTypes.name(AssetType.ANIMATION)
        AssetType.ROJO_PROJECT -> Txt.assetTypes.name(AssetType.ROJO_PROJECT)
    }

    ModalDialog(
        visible = visible,
        onCloseRequest = onCloseRequest,
        title = Txt.syncModal.title,
        dismissOnBackdropClick = true,
        dismissOnEsc = true,
        footer = ModalDialogConfirmActions(
            confirmText = Txt.syncModal.confirm,
            cancelText = Txt.common.cancel,
            onConfirm = { handleConfirm() },
            onCancel = onCloseRequest,
            confirmVariant = AppButtonVariant.PRIMARY,
            cancelVariant = AppButtonVariant.OUTLINE,
            confirmLoading = isSyncing
        )
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(1.0.cssRem)) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(0.75.cssRem).margin(bottom = 0.25.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    displayPrompt,
                    Modifier
                        .fontSize(1.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .color(palette.textPrimary)
                )
                Div(SyncModalTypeChipStyle.toModifier().toAttrs()) {
                    SpanText(Txt.assetTypes.name(asset.assetType))
                }
                StatusChip(asset.status)
            }

            Row(
                modifier = Modifier.fillMaxWidth().gap(0.375.cssRem).margin(bottom = 0.5.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    "🆔 $shortId",
                    Modifier
                        .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                        .fontSize(0.75.cssRem)
                        .color(palette.textSecondary)
                )
                AppButton(
                    text = "",
                    iconLeft = "📋",
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    onClick = { copyToClipboard(asset.id) }
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth().gap(0.5.cssRem).margin(bottom = 0.75.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    "$typeIcon $typeLabel",
                    Modifier
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .color(palette.textPrimary)
                )
            }

            Box(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(palette.surface)
                    .padding(0.75.cssRem)
                    .borderRadius(0.375.cssRem)
                    .borderLeft(3.px, LineStyle.Solid, palette.statusPending)
            ) {
                Column(modifier = Modifier.fillMaxWidth().gap(0.375.cssRem)) {
                    SpanText(
                        Txt.syncModal.warning,
                        Modifier
                            .fontSize(0.875.cssRem)
                            .lineHeight(1.45)
                            .color(palette.textPrimary)
                    )
                    SpanText(
                        Txt.syncModal.averageDelay,
                        Modifier
                            .fontSize(0.75.cssRem)
                            .color(palette.textSecondary)
                    )
                }
            }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.gap(0.5.cssRem).margin(top = 0.25.cssRem)
            ) {
                Input(
                    type = InputType.Checkbox,
                    attrs = {
                        checked(skipConfirm)
                        onChange { evt ->
                            skipConfirm = evt.value
                            setSkipSyncConfirmEnabled(evt.value)
                        }
                    }
                )
                SpanText(
                    Txt.syncModal.dontShowAgain,
                    Modifier
                        .fontSize(0.75.cssRem)
                        .color(palette.textSecondary)
                )
            }
        }
    }
}
